/**
 * Stage 13 — Report (simplified · Feb 2026).
 *
 * One-click DOCX / PDF export. No CP/QP sign-off gate, no 423 lock — the
 * user wanted an instant flow. The AI narrative is generated in the
 * background if missing and never blocks export.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { formatTonnage } from "@/components/ssot/OreFigures";
import SearchMultiplierSlider from "@/components/SearchMultiplierSlider";
import StageModeSwitcher, { useStageMode } from "@/components/StageModeSwitcher";

const STANDARDS = [
  { id: "JORC_2012",  label: "JORC 2012",  region: "Australasia · ASX/NZX",
    body: "Joint Ore Reserves Committee (AusIMM · AIG · MCA)",
    signoff: "Competent Person (CP)" },
  { id: "NI_43_101",  label: "NI 43-101",  region: "Canada · TSX/TSX-V",
    body: "Canadian Securities Administrators (CSA)",
    signoff: "Qualified Person (QP · P.Geo / P.Eng)" },
  { id: "SK_1300",    label: "SK-1300",    region: "USA · SEC-registered",
    body: "U.S. Securities and Exchange Commission",
    signoff: "Qualified Person (QP)" },
  { id: "CRIRSCO",    label: "CRIRSCO",    region: "International · umbrella",
    body: "Committee for Mineral Reserves Intl. Reporting Standards",
    signoff: "Competent / Qualified Person (CP/QP)" },
  { id: "SAMREC",     label: "SAMREC 2016", region: "South Africa · JSE",
    body: "South African Mineral Resource Committee",
    signoff: "Competent Person (CP · SACNASP / ECSA)" },
  { id: "SME",        label: "SME Guide",  region: "USA · SME-recommended",
    body: "Society for Mining, Metallurgy & Exploration",
    signoff: "Competent Person (SME Registered)" },
  { id: "internal",   label: "Internal Memo", region: "Non-regulatory",
    body: "SMIN auto-reporter",
    signoff: "Project geologist" },
];

function fmtT(t) { return formatTonnage(t, "auto"); }

export default function Stage13ReportCompliance({ projectId, onCompleted }) {
  const [_smMode, _setSmMode] = useStageMode(projectId, 13, "ai");
  const [rs, setRs] = useState(null);
  const [project, setProject] = useState(null);
  const [standard, setStandard] = useState("JORC_2012");
  const [searchMult, setSearchMult] = useState(2.0);
  const [rsLoading, setRsLoading] = useState(false);
  const [genPdf, setGenPdf] = useState(false);
  const [genDocx, setGenDocx] = useState(false);
  const [generated, setGenerated] = useState({ pdf: false, docx: false });
  const [aiGenerating, setAiGenerating] = useState(false);
  // Feb-2026 · Session I-30 — power-user narrative refresh
  const [aiStatus, setAiStatus] = useState(null);
  const [aiRegen, setAiRegen] = useState(false);

  const refreshAiStatus = () => {
    api.get(`/projects/${projectId}/ai-report/status?style=jorc_2012`)
      .then(({ data }) => setAiStatus(data)).catch(() => setAiStatus(null));
  };

  const loadResource = (mult = searchMult) => {
    setRsLoading(true);
    api.get(`/projects/${projectId}/resource-statement?search_multiplier=${mult}`)
      .then(({ data }) => setRs(data))
      .catch(() => setRs(null))
      .finally(() => setRsLoading(false));
  };

  useEffect(() => {
    if (!projectId) return;
    loadResource();
    api.get(`/projects/${projectId}`)
      .then(({ data }) => setProject(data))
      .catch(() => setProject(null));
    // Fire-and-forget AI narrative refresh — never blocks the user
    refreshAiStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Poll an ensure-fresh job until it terminates. Never rejects — surfaces
  // status via toast/log only so a slow LLM never blocks the export UI.
  const _pollJob = async (jobId, { intervalMs = 3000, maxTries = 90 } = {}) => {
    for (let i = 0; i < maxTries; i++) {
      await new Promise((r) => setTimeout(r, intervalMs));
      try {
        const { data } = await api.get(
          `/projects/${projectId}/ai-report/jobs/${jobId}`,
        );
        if (data.status === "completed") return { ok: true, result: data.result };
        if (data.status === "failed")    return { ok: false, error: data.error };
      } catch (e) {
        return { ok: false, error: e?.response?.data?.detail || e.message };
      }
    }
    return { ok: false, error: "Timed out waiting for AI narrative job" };
  };

  useEffect(() => {
    if (aiStatus && !aiStatus.all_fresh && !aiGenerating) {
      setAiGenerating(true);
      // Kicks off a background job — the response is 202 with a job_id.
      api.post(`/projects/${projectId}/ai-report/ensure-fresh?style=jorc_2012`)
        .then(async ({ data }) => {
          if (data?.job_id) {
            await _pollJob(data.job_id);
          }
        })
        .catch(() => { /* silent — this is a passive background refresh */ })
        .finally(() => { setAiGenerating(false); refreshAiStatus(); });
    }
  }, [aiStatus?.all_fresh]);

  const forceRegenerateAi = async () => {
    setAiRegen(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/ai-report/ensure-fresh?style=jorc_2012&force=true`,
      );
      if (!data?.job_id) {
        toast.error("Regenerate failed: no job id returned");
        return;
      }
      toast.success("AI narrative regeneration started — polling for completion…");
      const res = await _pollJob(data.job_id);
      if (res.ok) toast.success("AI narrative regenerated");
      else        toast.error(`Regenerate failed: ${res.error}`);
      refreshAiStatus();
    } catch (e) {
      toast.error(`Regenerate failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setAiRegen(false);
    }
  };

  const ws = rs?.waste_summary || {};

  const downloadBlob = async (url, filename, { method = "POST", body = {} } = {}) => {
    const resp = method === "GET"
      ? await api.get(url, { responseType: "blob" })
      : await api.post(url, body, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
  };

  // Poll a report-job until it's completed / failed. Returns
  // {ok, filename?, error?}. Never throws.
  const _pollReportJob = async (jobId, { intervalMs = 4000, maxTries = 90 } = {}) => {
    for (let i = 0; i < maxTries; i++) {
      await new Promise((r) => setTimeout(r, intervalMs));
      try {
        const { data } = await api.get(
          `/projects/${projectId}/report-jobs/${jobId}`,
        );
        if (data.status === "completed") return { ok: true, filename: data.filename };
        if (data.status === "failed")    return { ok: false, error: data.error };
      } catch (e) {
        // transient — keep polling
      }
    }
    return { ok: false, error: "Timed out waiting for report job" };
  };

  const exportPdf = async () => {
    setGenPdf(true);
    try {
      // Kick off the async job (GET ?async=true → HTTP 202 with job_id).
      const { data: job } = await api.get(
        `/projects/${projectId}/export/report.pdf?async=true&style=${encodeURIComponent(standard)}&search_multiplier=${searchMult}`,
      );
      if (!job?.job_id) throw new Error("Report job could not be started");
      toast.info("Report generating in background — this can take a few minutes for large projects.");

      const res = await _pollReportJob(job.job_id);
      if (!res.ok) throw new Error(res.error || "Report job failed");

      // Stream the finished PDF as a blob.
      const dl = await api.get(
        `/projects/${projectId}/report-jobs/${job.job_id}/download`,
        { responseType: "blob", timeout: 120000 },
      );
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || `smin_report_${projectId}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);

      setGenerated((g) => ({ ...g, pdf: true }));
      toast.success("PDF exported");
      await api.post(`/projects/${projectId}/pipeline-state/13/complete`, {
        output_summary: { format: "pdf", standard },
      }).catch(() => {});
      onCompleted?.();
    } catch (e) {
      toast.error(`PDF export failed: ${e.response?.data?.detail || e.message}`);
    } finally { setGenPdf(false); }
  };

  const exportDocx = async () => {
    setGenDocx(true);
    try {
      await downloadBlob(
        `/projects/${projectId}/export/report.docx`,
        `smin_report_${projectId}.docx`,
        { method: "POST", body: { standard, report_type: "internal", revision: "1" } },
      );
      setGenerated((g) => ({ ...g, docx: true }));
      toast.success("DOCX exported");
      await api.post(`/projects/${projectId}/pipeline-state/13/complete`, {
        output_summary: { format: "docx", standard },
      }).catch(() => {});
      onCompleted?.();
    } catch (e) {
      toast.error(`DOCX export failed: ${e.response?.data?.detail || e.message}`);
    } finally { setGenDocx(false); }
  };

  const selectedStandard = STANDARDS.find((s) => s.id === standard) || STANDARDS[0];

  return (
    <div className="space-y-6" data-testid="stage13-content">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-neutral-100">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">Input mode</div>
        <StageModeSwitcher mode={_smMode} onChange={_setSmMode} testidPrefix="stage13-mode" />
      </div>
      {aiGenerating && (
        <div className="border border-[#4CAF50]/40 bg-[#4CAF50]/10 rounded p-3 text-[12px] text-[#2E7D32] flex items-center gap-2"
             data-testid="stage13-ai-loading-banner">
          <Sparkles className="h-4 w-4 text-[#4CAF50] animate-pulse shrink-0" />
          <span>
            Preparing AI narrative in the background — you can export the report anytime.
          </span>
        </div>
      )}

      {/* SSoT tonnage tile */}
      {rs && (
        <div className="border border-neutral-200 rounded-md bg-neutral-50 p-4"
             data-testid="stage13-ssot-preview">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">Report pulls from</div>
              <div className="text-[13px] text-neutral-900">
                class_summary → <code className="font-mono">{rs.block_model_name || "—"}</code> · declared cutoff {(rs.declared_cutoff || 0).toFixed(3)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <MiniStat label="Ore tonnage" value={fmtT(ws.ore_tonnage)} testid="stage13-preview-ore" />
            <MiniStat label="Waste (incl. overburden)" value={fmtT(ws.waste_tonnage)} testid="stage13-preview-waste" />
            <MiniStat label="Strip ratio (W/O)" value={(Number(ws.strip_ratio) || 0).toFixed(3)} testid="stage13-preview-strip" />
          </div>
        </div>
      )}

      {/* Drilled-envelope filter · shared slider component with mini-preview chart */}
      <SearchMultiplierSlider
        projectId={projectId}
        value={searchMult}
        onChange={setSearchMult}
        onFilterMeta={() => loadResource(searchMult)}
      />

      {/* Compliance standard picker — prominent card so user knows a
          standard MUST be selected before generating a report */}
      <div className="border border-[#4CAF50]/40 rounded-md bg-neutral-50 p-5" data-testid="stage13-template">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4CAF50]">Step 1 · Choose a reporting framework</div>
            <div className="text-[15px] font-medium text-neutral-900 mt-1">International compliance standard</div>
            <div className="text-[11.5px] text-neutral-500 mt-1">
              The chosen standard governs terminology, classification tiers, and the sign-off
              role required before public disclosure of Mineral Resources / Reserves.
            </div>
          </div>
          <div className="text-[10.5px] font-mono text-emerald-600 border border-emerald-500 rounded px-2 py-1">
            Selected · {STANDARDS.find((s) => s.id === standard)?.label}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {STANDARDS.map((s) => {
            const active = standard === s.id;
            return (
              <button key={s.id}
                onClick={() => setStandard(s.id)}
                className={`text-left p-3 rounded border transition ${
                  active
                    ? "bg-[#4CAF50]/15 border-[#4CAF50] text-[#1B5E20]"
                    : "border-neutral-200 hover:border-neutral-400 text-neutral-800"
                }`}
                data-testid={`stage13-standard-${s.id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] font-medium">{s.label}</span>
                  {active && <CheckCircle2 className="h-3.5 w-3.5 text-[#4CAF50] shrink-0" />}
                </div>
                <div className="text-[10.5px] text-neutral-500 mt-0.5">{s.region}</div>
                <div className="text-[10px] text-neutral-500 mt-2 leading-snug">
                  {s.body}
                </div>
                <div className="text-[10px] text-[#2E7D32] mt-1.5 font-mono">
                  Sign-off: {s.signoff}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2 · Report cover preview thumbnail (Session I-32).
          A lightweight in-browser mock of the PDF cover so users can
          confirm the standard / branding / project name BEFORE spending
          10-20 seconds on the actual PDF generation. */}
      <ReportCoverPreview
        standard={selectedStandard}
        project={project}
        rs={rs}
      />

      {/* One-click export actions (Step 3) */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#4CAF50] mb-2">Step 3 · Generate the report</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="stage13-exports">
        <ExportCard
          label="DOCX report"
          desc="Native Word document — Heading styles, dynamic TOC, tables, embedded charts, AI narrative sections. Best for editing."
          onClick={exportDocx}
          busy={genDocx}
          done={generated.docx}
          testid="stage13-export-docx"
          primary
        />
        <ExportCard
          label="PDF report"
          desc="Print-ready PDF with charts and tables. Standard-compliant layout."
          onClick={exportPdf}
          busy={genPdf}
          done={generated.pdf}
          testid="stage13-export-pdf"
        />
        </div>
      </div>

      {/* Power-user AI narrative controls */}
      <div className="border border-neutral-200 rounded-md p-4 bg-neutral-50"
           data-testid="stage13-ai-narrative">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4CAF50] flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> AI narrative
            </div>
            <div className="text-[13px] text-neutral-900 mt-1 font-medium">
              Refresh the AI-authored report sections
            </div>
            <div className="text-[11.5px] text-neutral-500 mt-1 leading-relaxed max-w-2xl">
              Every export embeds AI-authored paragraphs (executive summary, geological
              interpretation, grade distribution, resource summary, QA/QC, risk, ESG).
              These are cached — click <em>Regenerate</em> to force-refresh with Claude
              Sonnet 4.5 before your next export.
            </div>
            {aiStatus && (
              <div className="text-[10.5px] font-mono text-neutral-500 mt-2">
                Status: {aiStatus.all_fresh ? (
                  <span className="text-emerald-600">✓ all sections fresh</span>
                ) : (
                  <span className="text-amber-600">↻ regenerating in background</span>
                )}
                {aiStatus.last_updated && (
                  <> · updated {aiStatus.last_updated.slice(0, 16).replace("T", " ")}</>
                )}
              </div>
            )}
          </div>
          <button
            onClick={forceRegenerateAi}
            disabled={aiRegen || aiGenerating}
            data-testid="stage13-ai-regenerate-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#4CAF50]/50 rounded text-[11.5px] text-[#4CAF50] hover:bg-[#4CAF50]/15 disabled:opacity-50 shrink-0"
          >
            {aiRegen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {aiRegen ? "Regenerating…" : "Regenerate now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="border border-neutral-200 rounded px-3 py-2.5 bg-neutral-50" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500">{label}</div>
      <div className="text-[15px] font-mono tabular-nums mt-1">{value}</div>
    </div>
  );
}

function ExportCard({ label, desc, onClick, busy, done, testid, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={`text-left border rounded-md p-5 transition group ${
        primary
          ? "border-[#4CAF50]/60 bg-[#4CAF50]/10 hover:bg-[#4CAF50]/15 hover:border-[#4CAF50]"
          : "border-neutral-200 hover:border-[#4CAF50]/60 bg-neutral-50"
      } disabled:opacity-60`}
      data-testid={testid}>
      <div className="flex items-center gap-2">
        {busy ? <Loader2 className="h-5 w-5 text-[#4CAF50] animate-spin" />
              : done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                     : <FileText className={`h-5 w-5 ${primary ? "text-[#4CAF50]" : "text-neutral-500 group-hover:text-[#4CAF50]"} transition`} />}
        <span className="text-[14px] text-neutral-900 font-medium">{label}</span>
        <Download className="h-4 w-4 text-neutral-500 ml-auto group-hover:text-[#4CAF50] transition" />
      </div>
      <div className="text-[11.5px] text-neutral-500 mt-2.5 leading-relaxed">{desc}</div>
    </button>
  );
}

/**
 * ReportCoverPreview — in-browser mock of the PDF cover page (Session I-32).
 * Renders instantly using project + standard state, avoids a full PDF
 * roundtrip just so the user can see what they're about to generate.
 * Matches the layout of `report.py :: _mk_smin_cover` fairly closely.
 */
function ReportCoverPreview({ standard, project, rs }) {
  const today = new Date().toISOString().slice(0, 10);
  const ws = rs?.waste_summary || {};
  const oreT = ws.ore_tonnage;
  const wasteT = ws.waste_tonnage;
  const stripR = ws.strip_ratio;

  const fmtT = (v) => {
    if (v == null || Number.isNaN(v)) return "—";
    const abs = Math.abs(v);
    if (abs >= 1e6) return `${(v / 1e6).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mt`;
    return `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t`;
  };

  return (
    <div data-testid="stage13-cover-preview">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#4CAF50] mb-2">
        Step 2 · Report cover preview
      </div>
      <div className="border border-neutral-200 rounded-md bg-neutral-50 p-3 md:p-4">
        <div className="flex flex-col md:flex-row gap-4 items-stretch">
          {/* Page-shaped card mimicking the actual cover */}
          <div className="relative shrink-0 mx-auto md:mx-0"
               style={{ width: 240, aspectRatio: "1 / 1.414" }}
               data-testid="stage13-cover-thumbnail">
            <div className="absolute inset-0 bg-white rounded-sm shadow-lg overflow-hidden font-sans text-neutral-900">
              {/* Orange accent stripe */}
              <div className="h-1.5 bg-[#4CAF50]" />
              <div className="px-3.5 pt-3 pb-1">
                <div className="text-[7.5px] uppercase tracking-[0.22em] text-neutral-500 font-mono">
                  {standard?.label || "Standard"}
                </div>
                <div className="text-[7px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
                  {standard?.region || ""}
                </div>
              </div>
              <div className="px-3.5 mt-2">
                {/* Mini brand block */}
                <div className="flex items-center gap-1.5">
                  <img src="/branding/smin-icon@4x.png" alt="SMIN" className="h-4 w-4 object-contain" />
                  <div className="text-[10px] font-black tracking-tight">SMIN</div>
                </div>
                <div className="text-[5.5px] uppercase tracking-[0.16em] mt-0.5 text-neutral-500 font-mono">
                  A product of Shanvi Resources
                </div>
              </div>
              <div className="px-3.5 mt-4">
                <div className="text-[6px] uppercase tracking-[0.2em] text-neutral-500 font-mono">Technical report</div>
                <div className="text-[13px] font-black tracking-tight mt-1 leading-tight">
                  {project?.name || "Project"}
                </div>
                <div className="text-[7px] font-mono text-neutral-500 mt-0.5">
                  {project?.location || "Location TBD"} · {project?.commodity || "—"}
                </div>
              </div>
              <div className="px-3.5 mt-3 text-[6.5px] font-mono text-neutral-500">
                Prepared under<br />
                <span className="text-neutral-800 font-semibold">{standard?.label}</span><br />
                Sign-off: {standard?.signoff}
              </div>
              <div className="absolute bottom-6 left-3.5 right-3.5 grid grid-cols-3 gap-1.5">
                <MiniCoverStat label="Ore" val={fmtT(oreT)} />
                <MiniCoverStat label="Waste" val={fmtT(wasteT)} />
                <MiniCoverStat label="Strip" val={stripR != null ? Number(stripR).toFixed(2) : "—"} />
              </div>
              <div className="absolute bottom-1.5 left-3.5 right-3.5 flex items-baseline justify-between text-[5.5px] font-mono text-neutral-500 uppercase tracking-[0.16em]">
                <span>Auto-generated</span><span>{today}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="flex-1 min-w-0 text-[12.5px] text-neutral-800 leading-relaxed">
            <div className="text-[13px] font-medium text-neutral-900 mb-1.5">
              Cover mock — confirm the branding, project and standard.
            </div>
            <div className="text-[11.5px] text-neutral-500">
              This is a live preview of the PDF cover page, built from the
              standard you selected and your project data. The full report
              generates ~40 pages of content behind this cover.
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-[10.5px]">
              <PreviewStat label="Project" value={project?.name || "—"} testid="cover-preview-project" />
              <PreviewStat label="Commodity" value={project?.commodity || "—"} testid="cover-preview-commodity" />
              <PreviewStat label="Standard" value={standard?.label} testid="cover-preview-standard" />
              <PreviewStat label="Sign-off" value={standard?.signoff} testid="cover-preview-signoff" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniCoverStat({ label, val }) {
  return (
    <div className="border border-neutral-200 rounded-sm bg-[#4CAF50]/10 px-1.5 py-1">
      <div className="text-[5.5px] uppercase tracking-[0.14em] text-neutral-500 font-mono">{label}</div>
      <div className="text-[8px] font-bold tabular-nums text-neutral-900 mt-0.5">{val}</div>
    </div>
  );
}

function PreviewStat({ label, value, testid }) {
  return (
    <div className="border border-neutral-200 rounded px-2.5 py-2 bg-neutral-50" data-testid={testid}>
      <div className="text-[9.5px] uppercase tracking-[0.14em] text-neutral-500">{label}</div>
      <div className="text-[11px] text-neutral-900 mt-0.5 truncate">{value}</div>
    </div>
  );
}
